import { Component, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { UIChart } from 'primeng/chart';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { ProspectsService } from '../shared/services/prospects.service';

export interface CustomerProspectSummaryDTO {
  customerName: string;
  prospectCount: number;
  activityCount: number;
}

@Component({
  selector: 'app-customer-prospect-summary',
  templateUrl: './customer-prospect-summary.component.html',
  styleUrl: './customer-prospect-summary.component.scss'
})
export class CustomerProspectSummaryComponent {
  @ViewChild('dtCustomerProspectSummary') table: Table;
  @ViewChild('customerProspectChart') customerProspectChart: UIChart;

  customerProspectSummary: CustomerProspectSummaryDTO[] = [];
  filteredCustomerProspectSummary: CustomerProspectSummaryDTO[] = [];
  customerProspectChartData: any;
  customerProspectChartOptions: any;
    // Track hidden datasets for legend
  hiddenDatasets: boolean[] = [];
  // Track which individual pie slices are hidden
  hiddenItems: boolean[] = [];

  selectedDataType: string = 'prospectCount';
  selectedCustomerScope: string = 'all';
  selectedCustomerCount: number = 1; // Changed to number
  isSorted: boolean = false;

  customerNamesOption : any [] ;


  dataTypes: any[] = [
    { label: 'Prospect Count', value: 'prospectCount' },
    { label: 'Activity Count', value: 'activityCount' }
  ];

  customerScopeOptions: any[] = [
    { label: 'Top', value: 'top' },
    { label: 'All', value: 'all' },
    { label: 'Bottom', value: 'bottom' }
  ];

  constructor(private prospectsService: ProspectsService) {}

  ngOnInit() {
    this.loadCustomerProspectSummary();
    this.initializeChartOptions();
  }

  loadCustomerProspectSummary() {
    this.prospectsService.getCustomerProspectSummary()
      .toPromise()
      .then(response => {
        this.customerProspectSummary = response;
        this.filteredCustomerProspectSummary = response;
        this.updateChartData();
      })
      .catch(error => {
        console.error('Error loading customer prospect summary:', error);
      });

    // Simulating API call with fake data
    // setTimeout(() => {
    //   this.customerProspectSummary = this.fakeData;
    //   this.filteredCustomerProspectSummary = this.fakeData;
    //   this.updateChartData();
    // }, 1000);
  }
  initializeChartOptions() {
    this.customerProspectChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false // Hide default legend, we'll use custom one
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              const label = tooltipItem.label;
              const value = tooltipItem.raw;
              return `${label}: ${value.toLocaleString()}`;
            }
          }
        }
      }
    };
  }
  updateChartData() {
    let dataToDisplay = [...this.filteredCustomerProspectSummary];

    // Apply sorting based on scope or sort toggle
    if (this.selectedCustomerScope === 'top' || (this.isSorted && this.selectedCustomerScope === 'all')) {
      dataToDisplay.sort((a, b) => b[this.selectedDataType] - a[this.selectedDataType]);
    } else if (this.selectedCustomerScope === 'bottom') {
      dataToDisplay.sort((a, b) => a[this.selectedDataType] - b[this.selectedDataType]);
    }

    // Limit to selected number of customers if scope is 'top' or 'bottom'
    if (this.selectedCustomerScope === 'top' || this.selectedCustomerScope === 'bottom') {
      dataToDisplay = dataToDisplay.slice(0, this.selectedCustomerCount);
    }

    const groupedData = this.groupDataByCustomer(dataToDisplay);
    const labels = Object.keys(groupedData);
    const data = labels.map(label => groupedData[label]);

    // Default colors
    const baseColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#4BC0C0', 
                        '#7986CB', '#FFA726', '#66BB6A', '#26C6DA', '#EC407A'];
    
    // Generate more colors if needed
    const backgroundColor = this.generateColors(baseColors, labels.length);
    const hoverBackgroundColor = [...backgroundColor]; // Same colors for hover

    this.customerProspectChartData = {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: backgroundColor,
        hoverBackgroundColor: hoverBackgroundColor
      }]
    };    // Reset hidden datasets tracking
    this.hiddenDatasets = new Array(1).fill(false);
    // Reset hidden items tracking
    this.hiddenItems = new Array(labels.length).fill(false);

    this.customerNamesOption = this.customerProspectSummary.map(customer => ({
      label:customer.customerName ,
      value:customer.customerName

    }));

    if (this.customerProspectChart) {
      this.customerProspectChart.reinit();
    }
  }

  // Helper method to generate colors for the chart
  generateColors(baseColors: string[], count: number): string[] {
    if (count <= baseColors.length) {
      return baseColors.slice(0, count);
    }
    
    // If we need more colors than the base set, generate them by cycling through the base set
    const colors = [...baseColors];
    for (let i = baseColors.length; i < count; i++) {
      colors.push(baseColors[i % baseColors.length]);
    }
    return colors;
  }

  groupDataByCustomer(data: CustomerProspectSummaryDTO[]) {
    return data.reduce((acc, item) => {
      acc[item.customerName] = item[this.selectedDataType];
      return acc;
    }, {});
  }

  onDataTypeChange() {
    this.updateChartData();
  }

  onCustomerScopeChange() {
    this.updateChartData();
  }

  onCustomerCountChange() {
    // Validate input: ensure it's a positive integer
    if (this.selectedCustomerCount < 1 || isNaN(this.selectedCustomerCount)) {
      this.selectedCustomerCount = 1;
    }
    // Optional: Cap at the number of available customers
    const maxCount = this.filteredCustomerProspectSummary.length;
    if (this.selectedCustomerCount > maxCount) {
      this.selectedCustomerCount = maxCount;
    }
    this.updateChartData();
  }

  onSortToggle() {
    this.isSorted = !this.isSorted;
    this.updateChartData();
  }

  onTableFilter(event: any) {
    this.filteredCustomerProspectSummary = event.filteredValue || [];
    this.updateChartData();
  }

  // Toggle visibility of a dataset when clicking on legend item
  toggleDataset(datasetIndex: number) {
    if (!this.customerProspectChart || !this.customerProspectChart.chart) {
      return;
    }

    // Initialize hiddenDatasets array if needed
    if (!this.hiddenDatasets || this.hiddenDatasets.length === 0) {
      this.hiddenDatasets = new Array(this.customerProspectChartData.datasets.length).fill(false);
    }

    // Toggle the hidden state
    this.hiddenDatasets[datasetIndex] = !this.hiddenDatasets[datasetIndex];
    
    // Apply the hidden state to the chart
    const meta = this.customerProspectChart.chart.getDatasetMeta(datasetIndex);
    meta.hidden = this.hiddenDatasets[datasetIndex];
    
    // Update the chart
    this.customerProspectChart.chart.update();
  }
  // Check if a dataset is currently hidden
  isDatasetHidden(datasetIndex: number): boolean {
    if (!this.hiddenDatasets || this.hiddenDatasets.length === 0) {
      return false;
    }
    return this.hiddenDatasets[datasetIndex];
  }
  
  // Toggle visibility of a specific pie chart segment
  toggleLegendItem(itemIndex: number) {
    // Initialize hiddenItems array if needed
    if (!this.hiddenItems || this.hiddenItems.length === 0) {
      this.hiddenItems = new Array(this.customerProspectChartData.labels.length).fill(false);
    }

    // Toggle the hidden state for this item
    this.hiddenItems[itemIndex] = !this.hiddenItems[itemIndex];
    
    if (this.customerProspectChart && this.customerProspectChart.chart) {
      // For pie/doughnut charts, we need to update the meta for the specific data point
      const meta = this.customerProspectChart.chart.getDatasetMeta(0);
      
      if (meta && meta.data && meta.data[itemIndex]) {
        // Toggle the hidden property of the specific slice
        meta.data[itemIndex].hidden = this.hiddenItems[itemIndex];
        
        // Update the chart
        this.customerProspectChart.chart.update();
      }
    }
  }
  
  // Check if a specific pie slice is hidden
  isItemHidden(itemIndex: number): boolean {
    if (!this.hiddenItems || this.hiddenItems.length === 0) {
      return false;
    }
    return this.hiddenItems[itemIndex];
  }

  exportSummaryPdf() {
    const doc = new jsPDF('landscape', 'pt', 'a4');

    const columns = [
      { header: '#', dataKey: 'index' },
      { header: 'Customer Name', dataKey: 'customerName' },
      { header: 'Prospect Count', dataKey: 'prospectCount' },
      { header: 'Activity Count', dataKey: 'activityCount' }
    ];

    const rows = this.filteredCustomerProspectSummary.map((row, index) => ({
      index: index + 1,
      customerName: row.customerName,
      prospectCount: row.prospectCount,
      activityCount: row.activityCount
    }));

    autoTable(doc, {
      columns: columns,
      body: rows,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 10,
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      },
      columnStyles: {
        index: { cellWidth: 30, halign: 'center' },
        customerName: { cellWidth: 100, halign: 'center' },
        prospectCount: { cellWidth: 60, halign: 'center' },
        activityCount: { cellWidth: 60, halign: 'center' }
      },
      margin: { top: 20 }
    });

    doc.save('CustomerProspectSummary.pdf');
  }

  exportSummaryExcel() {
    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(this.filteredCustomerProspectSummary.map(summary => ({
        'Customer Name': summary.customerName,
        'Prospect Count': summary.prospectCount,
        'Activity Count': summary.activityCount
      })));

      const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.saveAsExcelFile(excelBuffer, "CustomerProspectSummary");
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  }

  exportChartAsImage() {
    if (this.customerProspectChart && this.customerProspectChart.chart) {
      const chart = this.customerProspectChart.chart;
      const canvas = chart.canvas;
      const ctx = canvas.getContext('2d');

      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const base64Image = canvas.toDataURL('image/png');
      ctx.restore();

      const link = document.createElement('a');
      link.href = base64Image;
      link.download = 'customer-prospect-chart.png';
      link.click();
    }
  }

  exportChartAsPDF() {
    if (this.customerProspectChart && this.customerProspectChart.chart) {
      const chart = this.customerProspectChart.chart;
      const canvas = chart.canvas;
      const ctx = canvas.getContext('2d');

      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const base64Image = canvas.toDataURL('image/png');
      ctx.restore();

      const imgWidth = 280;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;

      const pdf = new jsPDF('landscape');
      pdf.addImage(base64Image, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save('customer-prospect-chart.pdf');
    }
  }
}
